import Config from 'react-native-config'
import { Storage } from '@src/utils'
import { markModalAsShown, shouldShowModalOnce } from '@src/hooks/useOneTimeVisitedState'
import { Device } from '@src/config'
import { ByosVideoCallsUpsellStep, OWN_SENSOR_GOAL_SURVEY_RESPONSE_MAP } from './constants'

export const recordByosVideoCallsUpsellInteraction = (
  step: ByosVideoCallsUpsellStep,
  userId: string,
) => {
  // Legacy-only (notification inbox OFF): persist client-side campaign progression.
  // Inbox ON uses `UserPropertyKind.ByosVideoCallsUpsell` instead and does not read these keys.
  Storage.set(`${Storage.BYOS_VIDEO_CALLS_UPSELL_LAST_STEP_KEY}_${userId}`, step)
  Storage.set(
    `${Storage.BYOS_VIDEO_CALLS_UPSELL_LAST_INTERACTION_TIMESTAMP_KEY}_${userId}`,
    Date.now(),
  )
}

export const getNextByosVideoCallsUpsellStep = (
  step: ByosVideoCallsUpsellStep,
): ByosVideoCallsUpsellStep | undefined => {
  // Backend orchestration treats modal dismissal and CTA click the same:
  // both should advance to an "after_*" marker step.
  // Backend jobs/orchestration then decide if/when to emit the next inbox notification.
  if (step === ByosVideoCallsUpsellStep.InitialModal) {
    return ByosVideoCallsUpsellStep.AfterInitialModal
  }

  if (step === ByosVideoCallsUpsellStep.FreeConsultationModal) {
    return ByosVideoCallsUpsellStep.AfterFreeConsultationModal
  }
}

export const getValidatedByosVideoCallsUpsellStep = (
  value?: string,
): ByosVideoCallsUpsellStep | undefined => {
  // Guard server data before rendering campaign UI.
  // Unknown values should fail closed (show nothing) rather than guessing.
  if (!value) {
    return
  }

  if (Object.values(ByosVideoCallsUpsellStep).includes(value as ByosVideoCallsUpsellStep)) {
    return value as ByosVideoCallsUpsellStep
  }
}

export const shouldShowByosVideoCallsUpsellSurface = ({
  notificationInboxEnabled,
  nextByosVideoCallsUpsellStep,
  step,
}: {
  notificationInboxEnabled: boolean
  nextByosVideoCallsUpsellStep?: ByosVideoCallsUpsellStep
  step: ByosVideoCallsUpsellStep
}) =>
  // BYOS cards/chatlike banners are local-only campaign surfaces.
  // When inbox is enabled, backend alert/notification kinds replace these surfaces.
  !notificationInboxEnabled && nextByosVideoCallsUpsellStep === step

export const getMinimumMinutesToWaitBetweenSteps = () => {
  const build = Config.ENV
  return build === 'production' ? 24 * 60 * 1 : 2
}

export const getMinimumMinutesToWaitToStartCampaign = () => {
  return 0 // start immediately
}

export const humanizeOwnSensorGoalSurveyAnswer = (answer: string) => {
  const mappedAnswer = OWN_SENSOR_GOAL_SURVEY_RESPONSE_MAP[answer]

  return mappedAnswer ?? answer.split('_').join(' ')
}

export const getHealthPermissionsModalStateInteractions = () => {
  return {
    shouldShow: () =>
      !Device.web && shouldShowModalOnce(Storage.HEALTH_PERMISSIONS_MODAL_VISITED_KEY),
    markAsShown: () => markModalAsShown(Storage.HEALTH_PERMISSIONS_MODAL_VISITED_KEY),
  }
}

export const getPermissionsModalStateInteractions = () => {
  return {
    shouldShow: () => shouldShowModalOnce(Storage.PERMISSIONS_MODAL_VISITED_KEY) && !Device.web,
    markAsShown: () => markModalAsShown(Storage.PERMISSIONS_MODAL_VISITED_KEY),
  }
}
